import { createSelector } from 'reselect'
import { getTourV2Items } from 'checkout/selectors/view/toursv2'
import { sortBy } from 'lib/array/arrayUtils'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import {
  SUPERIOR_PACKAGE_OPTIONS_AVAILABLES,
  TOUR_V2_TARGET_OCCUPANT_TYPES,
  TourOptionPackageUpgrades,
} from 'constants/tours'
import generateDepartureRoomPriceBreakdown from 'lib/tours/generateDepartureRoomPriceBreakdown'
import {
  getPackageOptionType,
  getRoomTypeCapacity,
  hasRoomTypeUpgrade,
  formatRoomType,
} from 'lib/tours/tourUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { isEmptyObject } from 'lib/object/isEmptyObject'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getAvailableFlightBenefits } from 'luxPlus/selectors/benefits/flights'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { isLuxPlusCheckoutStepAvailable } from './navigationSelectors'
import { isTourFlightUpsellAvailable } from './tourFlightSelectors'
import { getIsPostPurchaseTourChangeDates } from './cartModeSelectors'
import { getTourV2CheckoutItems } from 'checkout/selectors/view/accommodation'
import { getTourV2ExperienceItems } from 'checkout/selectors/view/tourV2ExperienceCartItems'
import { resolveTourV2OfferFromStore } from 'lib/tours/resolveTourV2OfferFromStore'
import config from 'constants/config'

export const getTourAvailablePackageDetails = createSelector(
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => state.offer.tourV2Offers,
  (items, tourOffers) => {
    const item = items[0]
    const tourOffer = resolveTourV2OfferFromStore(tourOffers, item?.offerId)
    return tourOffer?.variations?.[item?.purchasableOption?.fkVariationId]?.availablePackageUpgrades ?? []
  },
)

export const getTourV2CheckoutOfferId = createSelector(
  (state: App.State) => getTourV2CheckoutItems(state),
  (state: App.State) => getTourV2ExperienceItems(state),
  (tourItems, experienceItems) => tourItems[0]?.offerId ?? experienceItems[0]?.offerId,
)

export const getTourV2Offer = createSelector(
  (state: App.State) => getTourV2CheckoutOfferId(state),
  (state: App.State) => state.offer.tourV2Offers,
  (offerId, tourOffers): App.Tours.TourV2Offer | undefined => {
    return resolveTourV2OfferFromStore(tourOffers, offerId)
  },
)

export const getTourPurchasableAndPriceOptionsByPackage = createSelector(
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => state.offer.tourV2Offers,
  (items, tourOffers): Map<string, Array<[App.Tours.TourV2OfferPurchasableOption, App.Tours.V2OfferDeparturePriceBreakdown]>> => {
    const purchasablePriceOptionsPackageMap = new Map<string, Array<[App.Tours.TourV2OfferPurchasableOption, App.Tours.V2OfferDeparturePriceBreakdown]>>()
    items.forEach(item => {
      tourOffers[item.offerId]?.purchasableOptions
        .filter(
          purchasableOption => purchasableOption.fkDepartureId === item.purchasableOption.fkDepartureId &&
            getRoomTypeCapacity(purchasableOption.roomType) === sumUpOccupancies([item.occupancy], TOUR_V2_TARGET_OCCUPANT_TYPES),
        )
        .forEach(purchasableOption => {
          const priceBreakdown = generateDepartureRoomPriceBreakdown(
            item.occupancy,
            purchasableOption,
            tourOffers[item.offerId],
          )
          const packageOptionType = getPackageOptionType(purchasableOption.roomType)
          const purchasableOptionsWithPriceBreakDownByOption = purchasablePriceOptionsPackageMap.get(packageOptionType) ?? []
          purchasableOptionsWithPriceBreakDownByOption.push([purchasableOption, priceBreakdown])
          purchasablePriceOptionsPackageMap.set(packageOptionType, purchasableOptionsWithPriceBreakDownByOption)
        })
    })
    purchasablePriceOptionsPackageMap.forEach((purchasableOptionsWithPriceBreakDown: Array<[App.Tours.TourV2OfferPurchasableOption, App.Tours.V2OfferDeparturePriceBreakdown]>, packageOption) => {
      purchasablePriceOptionsPackageMap.set(packageOption, sortBy(
        purchasableOptionsWithPriceBreakDown,
        ([purchasableOption, _]) => purchasableOption.price,
        'asc',
      ))
    })
    return purchasablePriceOptionsPackageMap
  },
)

export const getTourV2InventorySummaryOfCartItems = createSelector(
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => state.offer.tourV2Offers,
  (items, tourOffers): Map<string, number> => {
    const departurePurchasableOptions = tourOffers?.[items[0]?.offerId]?.purchasableOptions.filter(purchasableOption => {
      return purchasableOption.fkDepartureId === items[0]?.purchasableOption.fkDepartureId // All items will have same departure Id
    })
    const roomTypeInventoryMap = new Map()
    departurePurchasableOptions?.forEach(purchasableOption => {
      roomTypeInventoryMap.set(purchasableOption.roomType, purchasableOption.inventoryLeft)
    })
    return roomTypeInventoryMap
  },
)

export const getIsTourItineraryPDFEnabled = createSelector(
  (state: App.State) => state.auth.account.isSpoofed,
  (isSpoofed): boolean => {
    return config.TOUR_ITINERARY_PDF_DOWNLOAD || isSpoofed
  },
)

export const getIsTourAccommodationPostPurchaseEnabled = createSelector(
  (state: App.State) => state.auth.account.isSpoofed,
  (isSpoofed): boolean => {
    return config.TOUR_ACCOMMODATION_POST_PURCHASE_ENABLED || isSpoofed
  },
)

export const getIsTourGenericItineraryPDFEnabled = createSelector(
  (state: App.State) => state.auth.account.isSpoofed,
  (isSpoofed): boolean => {
    return config.TOUR_GENERIC_ITINERARY_DOWNLOAD || isSpoofed
  },
)

export const getTourSelectablePurchasableOptionsByOccupant = createSelector(
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => state.offer.tourV2Offers,
  (items, tourOffers): Map<string, Array<App.Tours.TourV2OfferPurchasableOption>> => {
    const selectablePurchasableOptionsOccupantMap = new Map()
    items.forEach(item => {
      const purchasableOptionsByDeparture = tourOffers?.[item.offerId]?.purchasableOptions.filter(purchasableOption =>
        purchasableOption.fkDepartureId === item.purchasableOption.fkDepartureId &&
        getRoomTypeCapacity(purchasableOption.roomType) === sumUpOccupancies([item.occupancy], TOUR_V2_TARGET_OCCUPANT_TYPES),
      )
      selectablePurchasableOptionsOccupantMap.set(JSON.stringify(item.occupancy), purchasableOptionsByDeparture)
    })
    return selectablePurchasableOptionsOccupantMap
  },
)

export const getTourSelectablePurchasableOptionsWithPriceByOccupant = createSelector(
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => state.offer.tourV2Offers,
  (items, tourOffers): Map<App.Occupants, Map<App.Tours.TourV2OfferPurchasableOption, App.Tours.V2OfferDeparturePriceBreakdown>> => {
    const selectablePurchasableOptionsOccupantMap = new Map()
    items.forEach(item => {
      const purchasableOptionsByDeparture = tourOffers?.[item.offerId]?.purchasableOptions.filter(purchasableOption =>
        purchasableOption.fkDepartureId === item.purchasableOption.fkDepartureId &&
        getRoomTypeCapacity(purchasableOption.roomType) === sumUpOccupancies([item.occupancy], TOUR_V2_TARGET_OCCUPANT_TYPES),
      ) ?? []
      const purchasableOptionsWithPriceBreakDownByOption = new Map()
      purchasableOptionsByDeparture.forEach(purchasableOption => {
        const priceBreakdown = generateDepartureRoomPriceBreakdown(item.occupancy, purchasableOption, tourOffers[item.offerId])
        purchasableOptionsWithPriceBreakDownByOption.set(purchasableOption, priceBreakdown)
      })
      selectablePurchasableOptionsOccupantMap.set(item.occupancy, purchasableOptionsWithPriceBreakDownByOption)
    })
    return selectablePurchasableOptionsOccupantMap
  },
)

export const getTourV2PackageOptionsMissingRoomType = createSelector(
  (state: App.State) => getTourPurchasableAndPriceOptionsByPackage(state),
  (purchasableOptionsByPackage): Map<string, string | undefined> => {
    const packageOptionsMissingRoomType = new Map<string, string | undefined>()

    const packageOptionsAvailableRoomsTypes = new Map<string, Array<string | undefined>>()
    purchasableOptionsByPackage.forEach((purchasableOptions, packageOption) => {
      const availableRooms = purchasableOptions.map(([purchasableOption]) => formatRoomType(purchasableOption.roomType))
      packageOptionsAvailableRoomsTypes.set(packageOption, availableRooms)
    })

    const availablePackageOptions = [...purchasableOptionsByPackage.keys()]
    packageOptionsAvailableRoomsTypes.forEach((availableRooms, packageOption) => {
      availablePackageOptions.forEach(packageOptionToCheck => {
        if (packageOptionToCheck !== packageOption) {
          const availableRoomsToCheck = packageOptionsAvailableRoomsTypes.get(packageOptionToCheck) ?? []
          const missingRooms = availableRoomsToCheck.filter(room => !availableRooms.includes(room))
          packageOptionsMissingRoomType.set(packageOption, missingRooms[0])
        }
      })
    })
    return packageOptionsMissingRoomType
  },
)

export const getSelectablePurchasableOptionsHasRoomTypeUpgrade = createSelector(
  (state: App.State) => getTourSelectablePurchasableOptionsByOccupant(state),
  (selectablePurchasableOptionsByOccupant: Map<string, Array<App.Tours.TourV2OfferPurchasableOption>>): boolean =>
    Array.from(selectablePurchasableOptionsByOccupant.values())
      .filter(purchasableOptions => purchasableOptions && purchasableOptions.length > 1)
      .flatMap<App.Tours.TourV2OfferPurchasableOption>(purchasableOption => purchasableOption)
      .some(purchasableOption => hasRoomTypeUpgrade(purchasableOption?.roomType)),
)

export const getHasMultiplePurchasableOptionsWithPackageUpgradeOrRoomTypeUpgrade = createSelector(
  (state: App.State) => getTourPurchasableAndPriceOptionsByPackage(state),
  (state: App.State) => getTourPackageOptions(state),
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => getCanViewLuxPlusBenefits(state),
  (
    purchasablePriceOptionsByPackage: Map<string, Array<[App.Tours.TourV2OfferPurchasableOption, App.Tours.V2OfferDeparturePriceBreakdown]>>,
    tourPackageOptions: Array<string>,
    tourItems: Array<App.Checkout.TourV2Item>,
    canViewLuxPlusBenefits: boolean,
  ): boolean => {
    // This selector is used to decide whether the checkout should include the
    // "Select your preferred package" flow (and show "Packages" as the step label).
    // We apply the same no-downgrade filtering used in `TourPackageCheckoutPage`.

    const getFromPriceForPackageOption = (packageOption: string): number | undefined => {
      const purchasableOptionPrices = purchasablePriceOptionsByPackage.get(packageOption)
      if (!purchasableOptionPrices || purchasableOptionPrices.length === 0) return undefined

      return purchasableOptionPrices.reduce<number | undefined>((lowest, [purchasableOption]) => {
        const effectiveFromPrice =
          canViewLuxPlusBenefits && purchasableOption.memberPrice
            ? purchasableOption.memberPrice
            : purchasableOption.price
        if (lowest === undefined || effectiveFromPrice < lowest) return effectiveFromPrice
        return lowest
      }, undefined)
    }

    const selectedItem = tourItems?.[0]
    const selectedPackageOptionType =
      selectedItem?.purchasableOption?.packageType ??
      getPackageOptionType(selectedItem?.purchasableOption?.roomType)

    const availablePackageOptions = tourPackageOptions.filter(packageOption => {
      return getFromPriceForPackageOption(packageOption) !== undefined
    })

    const selectedFromPrice =
      selectedPackageOptionType
        ? getFromPriceForPackageOption(selectedPackageOptionType)
        : undefined

    const filteredPackageOptions =
      (selectedFromPrice !== undefined && selectedPackageOptionType !== undefined)
        ? availablePackageOptions.filter(packageOption => {
            if (packageOption === selectedPackageOptionType) return true
            const fromPrice = getFromPriceForPackageOption(packageOption)
            if (fromPrice === undefined) return true
            return fromPrice >= selectedFromPrice
          })
        : availablePackageOptions

    return filteredPackageOptions.length > 1
  },
)

export const getHasOptionalExtras = createSelector(
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => state.offer.tourV2Offers,
  (items, tourOffers): boolean => {
    return items.some(item => !isEmptyObject(tourOffers?.[item.offerId]?.purchasableExperienceOptions ?? EmptyObject))
  },
)

export const getIsLuxPlusToursEnabled = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => state.optimizely.optimizelyFeatureFlags,
  (isLuxPlusEnabled, optimizelyFeatureFlags) => !!(isLuxPlusEnabled && optimizelyFeatureFlags[OptimizelyFeatureFlags.luxPlusToursToggle]),
)

const getHasMemberPrice = createSelector(
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => state.offer.tourV2Offers,
  (items, tourOffers): boolean => {
    return items.some(item => {
      return tourOffers?.[item.offerId]?.purchasableOptions?.some(purchasableOption => !!purchasableOption?.memberPrice) ?? false
    })
  },
)

const getIsEarlyAccessForMembers = createSelector(
  (state: App.State) => getTourV2Offer(state),
  (offer): boolean => {
    return offer?.luxPlus?.access === 'earlyAccess'
  },
)

export const getShouldShowLuxPlusUpsellForTour = createSelector(
  (state: App.State) => getIsLuxPlusToursEnabled(state),
  (state: App.State) => getHasMemberPrice(state),
  (state: App.State) => getAvailableFlightBenefits(state).totalMemberPriceSavings,
  (state: App.State) => isTourFlightUpsellAvailable(state),
  (state: App.State) => isLuxPlusCheckoutStepAvailable(state),
  (state: App.State) => getIsEarlyAccessForMembers(state),
  (isLuxPlusToursEnabled, hasMemberPrice, flightMemberSavings, isTourFlightUpsellAvailable, isLuxPlusCheckoutStepAvailable, isEarlyAccessForMembers): boolean =>
    isLuxPlusCheckoutStepAvailable && (
      (isLuxPlusToursEnabled && (hasMemberPrice || isEarlyAccessForMembers)) ||
      (isTourFlightUpsellAvailable && flightMemberSavings > 0)
    ),
)

export const getTourPackageOptions = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder?.tourItems,
  (state: App.State) => getIsPostPurchaseTourChangeDates(state),
  (orderTourItems: Array<App.Tours.TourV2OrderItem> | undefined, isPostPurchaseTourChangeDates): Array<string> => {
    if (isPostPurchaseTourChangeDates && orderTourItems?.[0]?.packageOption?.packageType) {
      // Return only better package options
      const existingPackageType = orderTourItems[0].packageOption.packageType
      return SUPERIOR_PACKAGE_OPTIONS_AVAILABLES[existingPackageType]
    }
    return TourOptionPackageUpgrades
  },
)
